import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

// Labels for the per-class payment flow built by backend/lib/race-class-signup.js (getRacePaymentFlow).
const PAYMENT_STEP_LABELS = {
    signupfee: 'Signup fee',
    signup: 'Entry fee',
    seeding: 'Seeding fee',
};
const PAYMENT_STEP_WHEN = {
    beforeApproval: 'must be paid before the signup is approved',
    afterApproval: 'paid after approval',
    afterSignup: 'paid after signup',
};

@observer
class RaceValidation extends Component {
    constructor(props) {
        super(props);
        this.state = {
            validation: null,
            loading: true,
            collapsed: true,
            expandedSections: {
                errors: true,
                warnings: true,
                paymentSetup: true,
                info: false,
                userAccess: true,
                missingUsers: true,
                duplicates: true,
            },
        };
    }

    componentDidMount() {
        this.loadValidation();
    }

    componentDidUpdate(prevProps) {
        const { raceStore } = this.props.stores;
        const { race } = raceStore;
        const prevRaceStore = prevProps.stores.raceStore;
        const prevRace = prevRaceStore.race;
        if (race?.id && race.id !== prevRace?.id) {
            this.loadValidation();
        }
    }

    loadValidation = async () => {
        const { raceStore } = this.props.stores;
        const { race } = raceStore;
        if (!race?.id) {
            return;
        }
        this.setState({ loading: true });
        const validation = await raceStore.validateRace(race.id);
        this.setState({ validation, loading: false });
    }

    toggleCollapsed = () => {
        this.setState({ collapsed: !this.state.collapsed });
    }

    toggleSection = (section) => {
        this.setState({
            expandedSections: {
                ...this.state.expandedSections,
                [section]: !this.state.expandedSections[section],
            },
        });
    }

    onClickEditUser = (userId) => {
        const { appState } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('adminEditUser', {
            height: drawerHeightMedium,
            id: userId,
            callback: () => {
                appState.toggleDrawer();
                this.loadValidation();
            },
        }, 1);
    }

    renderCheckpointFlow = (checkpointFlow) => {
        if (!checkpointFlow) return null;
        return (
            <div class='mt-2 ps-3 border-start border-2'>
                <div class='mb-1'>
                    <strong>Flow:</strong> <span class='text-muted font-monospace' style='font-size: 0.85em;'>{checkpointFlow.flow}</span>
                </div>
                <div class='d-flex flex-wrap gap-2 mb-1'>
                    <span class='badge bg-secondary'>{checkpointFlow.totalCheckpoints} checkpoints</span>
                    {checkpointFlow.restStops > 0 && (
                        <span class='badge bg-info'>{checkpointFlow.restStops} mandatory rest</span>
                    )}
                    {checkpointFlow.optionalRests > 0 && (
                        <span class='badge text-bg-dark'>{checkpointFlow.optionalRests} optional rest</span>
                    )}
                    {checkpointFlow.flexibleRestCheckpoints > 0 && (
                        <span class='badge bg-success'>{checkpointFlow.flexibleRestCheckpoints} flexible rest</span>
                    )}
                    {checkpointFlow.useRemainingRestCheckpoints > 0 && (
                        <span class='badge bg-primary'>{checkpointFlow.useRemainingRestCheckpoints} remaining rest</span>
                    )}
                    {checkpointFlow.timeEqualizations > 0 && (
                        <span class='badge bg-warning text-dark'>{checkpointFlow.timeEqualizations} time eq</span>
                    )}
                    {checkpointFlow.geofenceEnabled > 0 && (
                        <span class='badge bg-info'>
                            <i class='fa-solid fa-satellite-dish me-1' />
                            {checkpointFlow.geofenceEnabled} GPS auto
                        </span>
                    )}
                </div>
                <div class='text-muted' style='font-size: 0.85em;'>
                    <strong>Distance:</strong> {checkpointFlow.distance} | <strong>Result mode:</strong> {checkpointFlow.resultModeDescription}
                </div>
                {checkpointFlow.restDetails && checkpointFlow.restDetails.length > 0 && (
                    <div class='mt-1 text-muted' style='font-size: 0.85em;'>
                        {checkpointFlow.restDetails.map(detail => (
                            <div key={detail}><i class='fa-solid fa-bed me-1' />{detail}</div>
                        ))}
                    </div>
                )}
                {checkpointFlow.flexibleRestDetails && checkpointFlow.flexibleRestDetails.length > 0 && (
                    <div class='mt-1 text-muted' style='font-size: 0.85em;'>
                        {checkpointFlow.flexibleRestDetails.map(detail => (
                            <div key={detail}><i class='fa-solid fa-arrows-left-right me-1' />{detail}</div>
                        ))}
                    </div>
                )}
                {checkpointFlow.geofenceDetails && checkpointFlow.geofenceDetails.length > 0 && (
                    <div class='mt-1 text-muted' style='font-size: 0.85em;'>
                        {checkpointFlow.geofenceDetails.map(detail => (
                            <div key={detail}><i class='fa-solid fa-satellite-dish me-1' />{detail}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { validation, loading, collapsed, expandedSections } = this.state;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        if (loading) {
            return (
                <div class={`mx-3 mt-2 mb-3 p-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-light'}`}>
                    <div class='d-flex align-items-center'>
                        <div class='spinner-border spinner-border-sm text-primary me-2' role='status'>
                            <span class='visually-hidden'>Loading...</span>
                        </div>
                        <span class='text-muted'>Loading validation...</span>
                    </div>
                </div>
            );
        }

        if (!validation) {
            return null;
        }

        const {
            errors = [], warnings = [], info = [], summary = {}, paymentSetup,
        } = validation;
        return (
            <div class={`mx-3 mt-2 mb-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-light'} ${validation.isValid ? 'border border-success' : 'border border-warning'}`}>
                {/* Header */}
                <div
                    class='d-flex flex-wrap align-items-center justify-content-between p-3 cursor-pointer'
                    onClick={this.toggleCollapsed}
                    style='cursor: pointer;'
                >
                    <div class='d-flex align-items-center'>
                        <i class={`fa-solid ${validation.isValid ? 'fa-circle-check text-success' : 'fa-triangle-exclamation text-warning'} me-2`} style='font-size: 1.2em;' />
                        <span class='fw-bold'>Race Validation</span>
                        {validation.isValid && (
                            <span class='badge bg-success ms-2'>Valid</span>
                        )}
                    </div>
                    <div class='d-flex align-items-center gap-2'>
                        {errors.length > 0 && (
                            <span class='badge bg-danger'>
                                <i class='fa-solid fa-times-circle me-1' />{errors.length} Error{errors.length !== 1 ? 's' : ''}
                            </span>
                        )}
                        {warnings.length > 0 && (
                            <span class='badge bg-warning text-dark'>
                                <i class='fa-solid fa-exclamation-triangle me-1' />{warnings.length} Warning{warnings.length !== 1 ? 's' : ''}
                            </span>
                        )}
                        {info.length > 0 && (
                            <span class='badge bg-info'>
                                <i class='fa-solid fa-info-circle me-1' />{info.length} Info
                            </span>
                        )}
                        <button
                            type='button'
                            class='btn btn-sm btn-link p-0 ms-2'
                            onClick={(e) => { e.stopPropagation(); this.loadValidation(); }}
                            title='Refresh validation'
                        >
                            <i class='fa-solid fa-refresh' />
                        </button>
                        <i class={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'} ms-2`} />
                    </div>
                </div>

                {/* Collapsible content */}
                {!collapsed && (
                    <div class='px-3 pb-3'>
                        {/* Summary info */}
                        <div class='d-flex flex-wrap gap-2 mb-3 text-muted' style='font-size: 0.85em;'>
                            <span><i class='fa-solid fa-flag me-1' />{summary.raceClassCount} race class{summary.raceClassCount !== 1 ? 'es' : ''}</span>
                            <span><i class='fa-solid fa-route me-1' />{summary.raceClassesWithGpx} with GPS</span>
                            <span><i class='fa-solid fa-map-marker-alt me-1' />{summary.raceClassesWithCheckpoints} with checkpoints</span>
                            {summary.waitingListCount > 0 && (
                                <span><i class='fa-solid fa-clock me-1' />{summary.waitingListCount} waiting list skipped</span>
                            )}
                        </div>

                        {/* Errors */}
                        {errors.length > 0 && (
                            <div class='mb-3'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('errors')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-times-circle text-danger me-2' />
                                    <span class='fw-bold text-danger'>Errors (must fix)</span>
                                    <i class={`fa-solid ${expandedSections.errors ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.errors && (
                                    <div class='ps-4'>
                                        {errors.map((err, idx) => (
                                            <div key={idx} class='mb-1 d-flex align-items-start'>
                                                <i class='fa-solid fa-circle text-danger me-2' style='font-size: 0.5em; margin-top: 0.5em;' />
                                                <span>{err.message}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Warnings */}
                        {warnings.length > 0 && (
                            <div class='mb-3'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('warnings')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-exclamation-triangle text-warning me-2' />
                                    <span class='fw-bold text-warning'>Warnings (should fix)</span>
                                    <i class={`fa-solid ${expandedSections.warnings ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.warnings && (
                                    <div class='ps-4'>
                                        {warnings.map((warn, idx) => (
                                            <div key={idx} class='mb-1 d-flex align-items-start'>
                                                <i class='fa-solid fa-circle text-warning me-2' style='font-size: 0.5em; margin-top: 0.5em;' />
                                                <span>{warn.message}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Payment setup */}
                        {paymentSetup && (
                            <div class='mb-3'>
                                <div
                                    class='d-flex flex-wrap align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('paymentSetup')}
                                    style='cursor: pointer;'
                                >
                                    <i class={`fa-solid fa-credit-card ${paymentSetup.required && !paymentSetup.ready ? 'text-danger' : 'text-success'} me-2`} />
                                    <span class={`fw-bold ${paymentSetup.required && !paymentSetup.ready ? 'text-danger' : 'text-success'}`}>
                                        Payment Setup
                                    </span>
                                    <span class={`badge ${paymentSetup.required ? 'bg-warning text-dark' : 'bg-secondary'} ms-2`}>
                                        {paymentSetup.required ? 'Required before approval' : 'Not required before approval'}
                                    </span>
                                    {paymentSetup.required && (
                                        <span class={`badge ${paymentSetup.ready ? 'bg-success' : 'bg-danger'} ms-2`}>
                                            {paymentSetup.ready ? 'Ready' : 'Missing setup'}
                                        </span>
                                    )}
                                    <i class={`fa-solid ${expandedSections.paymentSetup ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.paymentSetup && (
                                    <div class='ps-4'>
                                        <div class='text-muted mb-2' style='font-size: 0.85em;'>
                                            Verified online payment providers
                                        </div>
                                        <div class='d-flex flex-wrap gap-2 mb-3'>
                                            {paymentSetup.providers.map(provider => (
                                                <div key={provider.id} class='border rounded px-2 py-1'>
                                                    <i class={`fa-solid ${provider.ready ? 'fa-circle-check text-success' : provider.enabled ? 'fa-triangle-exclamation text-warning' : 'fa-circle-xmark text-muted'} me-1`} />
                                                    <span class='fw-bold'>{provider.name}</span>
                                                    <span class={`badge ${provider.ready ? 'bg-success' : provider.enabled ? 'bg-warning text-dark' : 'bg-secondary'} ms-2`}>
                                                        {provider.ready ? 'Ready' : provider.enabled ? 'Incomplete' : 'Disabled'}
                                                    </span>
                                                    {provider.enabled && !provider.ready && provider.missingFields.length > 0 && (
                                                        <div class='text-muted mt-1' style='font-size: 0.8em;'>
                                                            Missing: {provider.missingFields.join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <div class='text-muted mb-2' style='font-size: 0.85em;'>
                                            Race class payment requirements
                                        </div>
                                        {paymentSetup.raceClasses.length === 0 && (
                                            <div class='text-muted'>No race classes to validate.</div>
                                        )}
                                        {paymentSetup.raceClasses.map(item => (
                                            <div key={item.raceClassId} class='d-flex align-items-start mb-2'>
                                                <i class={`fa-solid ${item.ready ? 'fa-circle-check text-success' : 'fa-circle-xmark text-danger'} me-2 mt-1`} />
                                                <div>
                                                    <div class='fw-bold'>{item.raceClassName}</div>
                                                    {item.amount > 0 ? (
                                                        <div>
                                                            {item.paymentType === 'signupfee' ? 'Signup fee' : 'Signup payment'}:{' '}
                                                            {item.amount} {item.currency}
                                                        </div>
                                                    ) : (
                                                        <div class={paymentSetup.required ? 'text-danger' : 'text-muted'}>
                                                            No signup fee or signup payment amount is set
                                                        </div>
                                                    )}
                                                    {item.methods.length > 0 ? (
                                                        <div class='mt-1'>
                                                            {item.methods.map(method => (
                                                                <span key={method} class='badge bg-primary me-1'>{method}</span>
                                                            ))}
                                                        </div>
                                                    ) : item.amount > 0 && (
                                                        <div class={paymentSetup.required ? 'text-danger' : 'text-muted'}>
                                                            No configured online payment method supports {item.currency}
                                                        </div>
                                                    )}
                                                    {item.flow?.steps?.length > 0 && (
                                                        <div class='mt-1' style='font-size: 0.9em;'>
                                                            <div class='text-muted'>Payment flow for the musher:</div>
                                                            {item.flow.steps.map((step, idx) => (
                                                                <div key={step.type}>
                                                                    {idx + 1}. {PAYMENT_STEP_LABELS[step.type] || step.type}{' '}
                                                                    {step.amount} {step.currency}, {PAYMENT_STEP_WHEN[step.when] || step.when}{' '}
                                                                    ({step.methods.length > 0 ? step.methods.join(', ') : 'no payment method available'})
                                                                </div>
                                                            ))}
                                                            {item.flow.doubleFee && (
                                                                <div class='text-danger'>
                                                                    Both signup fee and entry fee are set, so the musher is asked to pay{' '}
                                                                    {item.flow.total} {item.currency} in total. Set one of them to 0 if that is not intended.
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Info */}
                        {info.length > 0 && (
                            <div class='mb-2'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('info')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-info-circle text-info me-2' />
                                    <span class='fw-bold text-info'>Checkpoint Logic</span>
                                    <i class={`fa-solid ${expandedSections.info ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.info && (
                                    <div class='ps-4'>
                                        {info.filter(i => i.category === 'checkpoint' && i.checkpointFlow).map((item, idx) => (
                                            <div key={idx} class='mb-3'>
                                                <div class='fw-bold'>{item.raceClassName}</div>
                                                {this.renderCheckpointFlow(item.checkpointFlow)}
                                            </div>
                                        ))}
                                        {info.filter(i => i.category === 'summary' || i.category === 'tracking').map((item, idx) => (
                                            <div key={idx} class='mb-1 d-flex align-items-start'>
                                                <i class='fa-solid fa-circle text-info me-2' style='font-size: 0.5em; margin-top: 0.5em;' />
                                                <span>{item.message}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* LiveRace data info */}
                        {validation.liveraceData && validation.liveraceData.hasOldData && (
                            <div class='mt-3 p-2 bg-warning bg-opacity-10 rounded'>
                                <div class='d-flex align-items-center'>
                                    <i class='fa-solid fa-satellite-dish text-warning me-2' />
                                    <span class='fw-bold'>LiveRace Data</span>
                                </div>
                                <div class='ps-4 mt-1 text-muted'>
                                    Found {validation.liveraceData.trackerCount} trackers with old data
                                    {validation.liveraceData.oldestDataDate && ` (oldest: ${new Date(validation.liveraceData.oldestDataDate).toLocaleDateString()})`}.
                                    Consider cleaning up old tracking data before race.
                                </div>
                            </div>
                        )}

                        {/* User Access Table - Admin only */}
                        {userStore.isAdmin && validation.userAccess && validation.userAccess.length > 0 && (
                            <div class='mb-3 mt-3'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('userAccess')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-users text-primary me-2' />
                                    <span class='fw-bold text-primary'>Users with Access ({validation.userAccess.length})</span>
                                    <i class={`fa-solid ${expandedSections.userAccess ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.userAccess && (
                                    <div class='table-responsive'>
                                        <table class='table table-sm table-striped mb-0'>
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Cellphone</th>
                                                    <th>Email</th>
                                                    <th class='text-center' title='User Approved'><i class='fa-solid fa-user-check' /></th>
                                                    <th class='text-center' title='Global Race Admin'><i class='fa-solid fa-crown' /></th>
                                                    <th class='text-center' title='Race Admin (this race)'><i class='fa-solid fa-user-shield' /></th>
                                                    <th class='text-center' title='Media Worker'><i class='fa-solid fa-camera' /></th>
                                                    <th class='text-center' title='Time Tracker'><i class='fa-solid fa-stopwatch' /></th>
                                                    <th class='text-center' title='Race Leader'><i class='fa-solid fa-flag-checkered' /></th>
                                                    <th class='text-center' title='TD Leader'><i class='fa-solid fa-clipboard-check' /></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {validation.userAccess.map((u, idx) => (
                                                    <tr key={idx}>
                                                        <td>
                                                            {userStore.isAdmin && u.userId && (
                                                                <button
                                                                    type='button'
                                                                    class='btn btn-sm btn-link p-0 me-2'
                                                                    onClick={() => this.onClickEditUser(u.userId)}
                                                                    title='Edit user'
                                                                >
                                                                    <i class='fa-solid fa-pen-to-square' />
                                                                </button>
                                                            )}
                                                            {u.firstname} {u.lastname}
                                                        </td>
                                                        <td class='text-muted' style='font-size: 0.9em;'>{u.cellphone}</td>
                                                        <td class='text-muted' style='font-size: 0.9em;'>{u.email}</td>
                                                        <td class='text-center'>
                                                            {u.isApproved ? (
                                                                <i class='fa-solid fa-check text-success' />
                                                            ) : (
                                                                <i class='fa-solid fa-xmark text-danger' />
                                                            )}
                                                        </td>
                                                        <td class='text-center'>
                                                            {u.userIsRaceAdmin ? (
                                                                <i class='fa-solid fa-check text-success' />
                                                            ) : u.isRaceAdmin ? (
                                                                <i class='fa-solid fa-xmark text-danger' title='Race admin without global permission' />
                                                            ) : null}
                                                        </td>
                                                        <td class='text-center'>{u.isRaceAdmin && <i class='fa-solid fa-check text-success' />}</td>
                                                        <td class='text-center'>{u.isMediaWorker && <i class='fa-solid fa-check text-success' />}</td>
                                                        <td class='text-center'>{u.isTimeTracker && <i class='fa-solid fa-check text-success' />}</td>
                                                        <td class='text-center'>{u.isRaceLeader && <i class='fa-solid fa-check text-success' />}</td>
                                                        <td class='text-center'>{u.isTdLeader && <i class='fa-solid fa-check text-success' />}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Missing Users Warning - Admin only */}
                        {userStore.isAdmin && validation.missingUsers && validation.missingUsers.length > 0 && (
                            <div class='mb-3'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('missingUsers')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-user-slash text-danger me-2' />
                                    <span class='fw-bold text-danger'>Missing User Records ({validation.missingUsers.length})</span>
                                    <i class={`fa-solid ${expandedSections.missingUsers ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.missingUsers && (
                                    <div class='ps-4'>
                                        <div class='text-muted mb-2' style='font-size: 0.85em;'>
                                            These users are assigned roles but don't have a corresponding user account:
                                        </div>
                                        {validation.missingUsers.map((u, idx) => (
                                            <div key={idx} class='mb-2 p-2 bg-danger bg-opacity-10 rounded'>
                                                <div class='fw-bold'>
                                                    <i class='fa-solid fa-user me-1' />
                                                    {u.firstname} {u.lastname}
                                                </div>
                                                <div class='text-muted' style='font-size: 0.9em;'>
                                                    <i class='fa-solid fa-phone me-1' />{u.cellphone}
                                                    {u.email && <span class='ms-2'><i class='fa-solid fa-envelope me-1' />{u.email}</span>}
                                                </div>
                                                <div class='mt-1' style='font-size: 0.85em;'>
                                                    Roles:
                                                    {u.roles.isRaceAdmin && <span class='badge bg-secondary ms-1'>Race Admin</span>}
                                                    {u.roles.isMediaWorker && <span class='badge bg-secondary ms-1'>Media</span>}
                                                    {u.roles.isTimeTracker && <span class='badge bg-secondary ms-1'>Time Tracker</span>}
                                                    {u.roles.isRaceLeader && <span class='badge bg-secondary ms-1'>Race Leader</span>}
                                                    {u.roles.isTdLeader && <span class='badge bg-secondary ms-1'>TD Leader</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Duplicate Users Warning - Admin only */}
                        {userStore.isAdmin && validation.duplicateUsers && validation.duplicateUsers.length > 0 && (
                            <div class='mb-3'>
                                <div
                                    class='d-flex align-items-center cursor-pointer mb-2'
                                    onClick={() => this.toggleSection('duplicates')}
                                    style='cursor: pointer;'
                                >
                                    <i class='fa-solid fa-exclamation-triangle text-danger me-2' />
                                    <span class='fw-bold text-danger'>Duplicate Cellphones ({validation.duplicateUsers.length} groups)</span>
                                    <i class={`fa-solid ${expandedSections.duplicates ? 'fa-chevron-up' : 'fa-chevron-down'} ms-2`} />
                                </div>
                                {expandedSections.duplicates && (
                                    <div class='ps-4'>
                                        {validation.duplicateUsers.map((dup, idx) => (
                                            <div key={idx} class='mb-2 p-2 bg-danger bg-opacity-10 rounded'>
                                                <div class='fw-bold mb-1'>
                                                    <i class='fa-solid fa-phone me-1' />
                                                    {dup.cellphone} ({dup.count} users)
                                                </div>
                                                <div class='ps-3'>
                                                    {dup.users.map((u, uidx) => (
                                                        <div key={uidx} class='text-muted' style='font-size: 0.9em;'>
                                                            ID: {u.id} - {u.firstname} {u.lastname} ({u.email || 'no email'})
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default RaceValidation;
